// Langelier Saturation Index (LSI) Calculator
// LSI = pH + TF + CF + AF - TDS_Factor

// Temperature Factor based on water temperature in Fahrenheit
const getTemperatureFactor = (tempF) => {
  const factors = [
    { temp: 32, factor: 0.0 },
    { temp: 37, factor: 0.1 },
    { temp: 46, factor: 0.2 },
    { temp: 53, factor: 0.3 },
    { temp: 60, factor: 0.4 },
    { temp: 66, factor: 0.5 },
    { temp: 76, factor: 0.6 },
    { temp: 84, factor: 0.7 },
    { temp: 94, factor: 0.8 },
    { temp: 105, factor: 0.9 }
  ];
  
  for (let i = 0; i < factors.length - 1; i++) {
    if (tempF >= factors[i].temp && tempF < factors[i + 1].temp) {
      // Linear interpolation
      const t1 = factors[i].temp;
      const t2 = factors[i + 1].temp;
      const f1 = factors[i].factor;
      const f2 = factors[i + 1].factor;
      return f1 + ((tempF - t1) / (t2 - t1)) * (f2 - f1);
    }
  }
  
  if (tempF >= 105) return 0.9;
  return 0.0;
};

// Calcium Hardness Factor
const getCalciumFactor = (calcium) => {
  if (calcium <= 25) return 1.0;
  if (calcium <= 50) return 1.3;
  if (calcium <= 75) return 1.5;
  if (calcium <= 100) return 1.6;
  if (calcium <= 125) return 1.7;
  if (calcium <= 150) return 1.8;
  if (calcium <= 200) return 1.9;
  if (calcium <= 250) return 2.0;
  if (calcium <= 300) return 2.1;
  if (calcium <= 400) return 2.2;
  if (calcium <= 800) return 2.5;
  return 2.6;
};

// Total Alkalinity Factor
const getAlkalinityFactor = (alkalinity) => {
  if (alkalinity <= 25) return 1.4;
  if (alkalinity <= 50) return 1.7;
  if (alkalinity <= 75) return 1.9;
  if (alkalinity <= 100) return 2.0;
  if (alkalinity <= 125) return 2.1;
  if (alkalinity <= 150) return 2.2;
  if (alkalinity <= 200) return 2.3;
  if (alkalinity <= 250) return 2.4;
  if (alkalinity <= 300) return 2.5;
  if (alkalinity <= 400) return 2.6;
  if (alkalinity <= 800) return 2.9;
  return 3.0;
};

// TDS Factor
const getTDSFactor = (tds) => {
  if (tds <= 1000) return 12.0;
  if (tds <= 2000) return 12.1;
  if (tds <= 3000) return 12.2;
  return 12.2;
};

export const calculateSaturationIndex = (reading) => {
  const ph = parseFloat(reading.ph);
  const temp = parseFloat(reading.water_temperature);
  const calcium = parseFloat(reading.calcium_hardness);
  const alkalinity = parseFloat(reading.alkalinity);
  const tds = parseFloat(reading.total_dissolved_solids);
  
  if (isNaN(ph) || isNaN(temp) || isNaN(calcium) || isNaN(alkalinity) || isNaN(tds)) {
    return null;
  }
  
  const TF = getTemperatureFactor(temp);
  const CF = getCalciumFactor(calcium);
  const AF = getAlkalinityFactor(alkalinity);
  const TDS_Factor = getTDSFactor(tds);
  
  const lsi = ph + TF + CF + AF - TDS_Factor;
  
  return parseFloat(lsi.toFixed(2));
};

export const getLSIStatus = (lsi) => {
  if (lsi === null || isNaN(lsi)) {
    return {
      status: 'unknown',
      label: 'N/A',
      description: 'Unable to calculate',
      color: 'text-gray-600 bg-gray-50 border-gray-200'
    };
  }
  
  if (lsi < -0.3) {
    return {
      status: 'corrosive',
      label: 'Corrosive',
      description: 'Water may corrode metal and plaster',
      color: 'text-red-600 bg-red-50 border-red-200',
      recommendation: 'Increase pH, alkalinity, or calcium hardness'
    };
  }
  
  if (lsi >= -0.3 && lsi <= 0.3) {
    return {
      status: 'balanced',
      label: 'Balanced',
      description: 'Water is properly balanced',
      color: 'text-green-600 bg-green-50 border-green-200',
      recommendation: 'Maintain current levels'
    };
  }
  
  return {
    status: 'scaling',
    label: 'Scale Forming',
    description: 'Calcium carbonate may precipitate',
    color: 'text-amber-600 bg-amber-50 border-amber-200',
    recommendation: 'Decrease pH, alkalinity, or calcium hardness'
  };
};
